import { useState, useEffect, useCallback } from "react";
import {
  MessageCircle,
  Sparkles,
  Plus,
  Trash2,
  Edit2,
  Loader2,
  Check,
  CheckCircle,
  AlertCircle,
  X,
} from "lucide-react";
import apiClient from "../lib/api-client";

interface FaqItem {
  question: string;
  answer: string;
}

interface SuggestedQuestion {
  id: string;
  question: string;
  answer: string;
}

interface VoiceAiFaqSectionProps {
  locationId: string;
  canSuggest: boolean;
}

export default function VoiceAiFaqSection({
  locationId,
  canSuggest,
}: VoiceAiFaqSectionProps) {
  const [faq, setFaq] = useState<FaqItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<SuggestedQuestion[]>([]);
  const [suggestMessage, setSuggestMessage] = useState<string | null>(null);
  const [showSuggestions, setShowSuggestions] = useState(false);

  const fetchFaq = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get(`/locations/${locationId}/faq`);
      setFaq((res.data.data as FaqItem[]) || []);
    } catch {
      setFaq([]);
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => {
    fetchFaq();
  }, [fetchFaq]);

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiClient.put(`/locations/${locationId}/faq`, { faq });
      setMessage({
        type: "success",
        text: "Preguntas frecuentes guardadas correctamente",
      });
      setEditingIndex(null);
      setTimeout(() => setMessage(null), 3000);
    } catch {
      setMessage({
        type: "error",
        text: "Error al guardar las preguntas frecuentes",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddItem = () => {
    const newIndex = faq.length;
    setFaq([...faq, { question: "", answer: "" }]);
    setEditingIndex(newIndex);
  };

  const handleRemoveItem = (index: number) => {
    setFaq(faq.filter((_, i) => i !== index));
    if (editingIndex === index) setEditingIndex(null);
    else if (editingIndex !== null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const handleUpdateItem = (
    index: number,
    field: "question" | "answer",
    value: string,
  ) => {
    const updated = [...faq];
    updated[index] = { ...updated[index], [field]: value };
    setFaq(updated);
  };

  const handleSuggest = async () => {
    setSuggesting(true);
    setSuggestMessage(null);
    setSuggestions([]);
    setShowSuggestions(true);
    try {
      const res = await apiClient.post(`/locations/${locationId}/faq/suggest`);
      const questions: { question: string; answer?: string }[] =
        res.data.data || [];
      if (questions.length === 0) {
        setSuggestMessage(
          res.data.message ||
            "No hay suficientes llamadas ni conversaciones de WhatsApp atendidas por tu equipo para generar sugerencias.",
        );
      } else {
        setSuggestions(
          // Si el análisis trae respuesta propuesta (sacada de lo que
          // contestó el equipo por WhatsApp), se precarga editable.
          questions.map((q, i) => ({
            id: `${Date.now()}-${i}`,
            question: q.question,
            answer: q.answer ?? "",
          })),
        );
      }
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      const msg = e.response?.data?.message ?? "";
      setSuggestMessage(
        msg ||
          "No hay suficientes llamadas para generar sugerencias. Necesitas al menos 5 llamadas grabadas.",
      );
    } finally {
      setSuggesting(false);
    }
  };

  const handleAddSuggestion = (suggestion: SuggestedQuestion) => {
    setFaq([
      ...faq,
      { question: suggestion.question, answer: suggestion.answer },
    ]);
    setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
  };

  const handleDiscardSuggestion = (id: string) => {
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  };

  const handleSuggestionAnswerChange = (id: string, answer: string) => {
    setSuggestions((prev) =>
      prev.map((s) => (s.id === id ? { ...s, answer } : s)),
    );
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Cargando preguntas frecuentes...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
        <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-gray-400" />
          Preguntas frecuentes
        </h3>
        {canSuggest && (
          <button
            onClick={handleSuggest}
            disabled={suggesting}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-opacity disabled:opacity-50"
            style={{
              background: "linear-gradient(135deg, #7c3aed 0%, #2563eb 100%)",
            }}
          >
            {suggesting ? (
              <>
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Analizando llamadas...
              </>
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5" />✨ Sugerir con IA
              </>
            )}
          </button>
        )}
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Añade las preguntas más frecuentes de tus clientes con sus respuestas.
        La IA las usará para responder automáticamente por teléfono.
      </p>

      {/* Feedback message */}
      {message && (
        <div
          className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            message.type === "success"
              ? "bg-green-50 text-green-700 border border-green-200"
              : "bg-red-50 text-red-700 border border-red-200"
          }`}
        >
          {message.type === "success" ? (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {message.text}
        </div>
      )}

      {/* AI Suggestions panel */}
      {showSuggestions && (
        <div className="mb-6 bg-purple-50 border border-purple-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-purple-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" />
              Sugerencias de la IA
            </h4>
            <button
              onClick={() => setShowSuggestions(false)}
              className="p-1 hover:bg-purple-100 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-purple-500" />
            </button>
          </div>

          {suggesting && (
            <div className="flex items-center gap-2 text-purple-700 text-sm py-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Analizando tus llamadas...
            </div>
          )}

          {!suggesting && suggestMessage && (
            <p className="text-sm text-purple-700">{suggestMessage}</p>
          )}

          {!suggesting && suggestions.length === 0 && !suggestMessage && (
            <div className="flex items-center gap-2 text-purple-700 text-sm">
              <CheckCircle className="w-4 h-4 text-green-600" />
              ¡Todo revisado! No hay más sugerencias pendientes.
            </div>
          )}

          {!suggesting && suggestions.length > 0 && (
            <div className="space-y-3">
              <p className="text-xs text-purple-700 mb-3">
                Escribe la respuesta para cada pregunta y haz clic en "Añadir"
                para agregarla a tu FAQ.
              </p>
              {suggestions.map((s) => (
                <div
                  key={s.id}
                  className="bg-white border border-purple-200 rounded-lg p-4 space-y-2"
                >
                  <p className="text-sm font-medium text-gray-800">
                    {s.question}
                  </p>
                  <textarea
                    value={s.answer}
                    onChange={(e) =>
                      handleSuggestionAnswerChange(s.id, e.target.value)
                    }
                    rows={2}
                    placeholder="Escribe la respuesta aquí..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm resize-none"
                  />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleAddSuggestion(s)}
                      disabled={!s.answer.trim()}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      <Check className="w-3.5 h-3.5" />
                      Añadir
                    </button>
                    <button
                      onClick={() => handleDiscardSuggestion(s.id)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <X className="w-3.5 h-3.5" />
                      Descartar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* FAQ List */}
      {faq.length === 0 && !showSuggestions && (
        <p className="text-sm text-gray-400 mb-4">
          Aún no has añadido ninguna pregunta frecuente.
        </p>
      )}

      <div className="space-y-3 mb-4">
        {faq.map((item, index) => {
          const isEditing = editingIndex === index;
          return (
            <div
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden"
            >
              {/* Item header */}
              <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                <span className="text-xs font-medium text-gray-500">
                  Pregunta {index + 1}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setEditingIndex(isEditing ? null : index)}
                    className="p-1.5 hover:bg-gray-200 rounded-lg transition-colors"
                    title={isEditing ? "Colapsar" : "Editar"}
                  >
                    <Edit2 className="w-3.5 h-3.5 text-gray-500" />
                  </button>
                  <button
                    onClick={() => handleRemoveItem(index)}
                    className="p-1.5 hover:bg-red-100 rounded-lg transition-colors"
                    title="Eliminar"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                  </button>
                </div>
              </div>

              {/* Item body */}
              {isEditing ? (
                <div className="p-4 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Pregunta
                    </label>
                    <input
                      type="text"
                      value={item.question}
                      onChange={(e) =>
                        handleUpdateItem(index, "question", e.target.value)
                      }
                      placeholder="Ej: ¿Tienen terraza?"
                      autoFocus
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Respuesta
                    </label>
                    <textarea
                      value={item.answer}
                      onChange={(e) =>
                        handleUpdateItem(index, "answer", e.target.value)
                      }
                      placeholder="Ej: Sí, tenemos una terraza abierta de mayo a octubre con capacidad para 30 personas."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                    />
                  </div>
                </div>
              ) : (
                <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Pregunta</p>
                    <p className="text-sm text-gray-800">
                      {item.question ? (
                        item.question
                      ) : (
                        <span className="text-gray-400 italic">
                          Sin pregunta
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 mb-1">Respuesta</p>
                    <p className="text-sm text-gray-800">
                      {item.answer ? (
                        item.answer
                      ) : (
                        <span className="text-gray-400 italic">
                          Sin respuesta
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Add button */}
      <button
        onClick={handleAddItem}
        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-200 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors mb-4"
      >
        <Plus className="w-4 h-4" />
        Añadir pregunta
      </button>

      {/* Save button */}
      {faq.length > 0 && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white rounded-lg font-medium text-sm hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Guardar FAQ
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
